export type SimpleV2OfframpStatus = 'complete' | 'failed' | 'not_reached'

export interface SimpleV2OfframpStatusItem {
  title: string
  description: string
}

export const simpleV2OfframpRowKeys = [
  'personalDetails',
  'bankAccountsAssets',
  'incomeEmployment',
  'propertyHousing',
  'identityCredit',
  'automatedReviewOptions',
] as const

export type SimpleV2OfframpRowKey = (typeof simpleV2OfframpRowKeys)[number]

export const simpleV2OfframpRows: Record<
  SimpleV2OfframpRowKey,
  Partial<Record<SimpleV2OfframpStatus, SimpleV2OfframpStatusItem>>
> = {
  personalDetails: {
    complete: {
      title: 'Your personal details',
      description: 'Verified and saved, no re-entering anything.',
    },
    not_reached: {
      title: 'Personal details',
      description: 'Your loan officer will confirm anything still missing.',
    },
  },
  bankAccountsAssets: {
    complete: {
      title: 'Bank accounts and asset balances',
      description: 'Connected through Plaid and available to review.',
    },
    failed: {
      title: "Couldn't verify assets",
      description:
        "We looked but couldn't confirm the right assets for this loan.",
    },
    not_reached: {
      title: 'Bank accounts and asset balances',
      description:
        'Still needs to be completed so we can confirm your available funds.',
    },
  },
  incomeEmployment: {
    complete: {
      title: 'Income and employment',
      description:
        'Pulled directly from your bank, no paystubs or statements needed.',
    },
    failed: {
      title: "Couldn't verify income/employment",
      description:
        "We tried to pull your income data — it just didn't come through.",
    },
    not_reached: {
      title: 'Income and employment',
      description:
        'Still needs to be completed so your loan officer can confirm your income.',
    },
  },
  propertyHousing: {
    complete: {
      title: 'Property and housing details',
      description: 'Your address and loan details are saved.',
    },
    failed: {
      title: 'No property info',
      description:
        "We didn't have enough property details to keep things moving.",
    },
    not_reached: {
      title: 'Property and housing details',
      description:
        'Still needs to be completed before loan options can be finalized.',
    },
  },
  identityCredit: {
    complete: {
      title: 'Identity and credit',
      description: 'Your identity details were saved and credit was checked.',
    },
    failed: {
      title: "Couldn't verify credit",
      description:
        "We weren't able to complete the credit check automatically.",
    },
    not_reached: {
      title: 'Identity and credit',
      description:
        'Still needs to be completed before we can finalize options.',
    },
  },
  automatedReviewOptions: {
    complete: {
      title: 'Automated review and loan options',
      description:
        'We completed the automated review and found available loan options.',
    },
    failed: {
      title: 'No offers available',
      description:
        "We checked every option and couldn't find a match right now.",
    },
    not_reached: {
      title: 'Automated review and loan options',
      description:
        'Still needs to be completed once the remaining details are confirmed.',
    },
  },
}
